import { createContext, useContext, useEffect, useState } from "react";
import api from "../api/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  setUser: () => {},
  login: () => {},
  logout: () => {},
  hasPermission: () => false,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("lumat_token");

    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const u = await api("/auth/me");

        if (!cancelled) {
          setUser(u);
        }
      } catch (err) {
        console.error("Auth check failed:", err);
        localStorage.removeItem("lumat_token");

        if (!cancelled) {
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (userData) => {
    if (userData?.token) {
      localStorage.setItem("lumat_token", userData.token);
    }

    try {
      const u = await api("/auth/me");
      setUser(u);
    } catch (err) {
      console.error("Failed to load user permissions after login:", err);

      localStorage.removeItem("lumat_token");
      setUser(null);

      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem("lumat_token");
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!permission) {
      return true;
    }

    if (!Array.isArray(user?.permissions)) {
      return false;
    }

    return user.permissions.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        loading,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}